/**
 * Market-Aware Cart Utilities
 * Provides locale-aware cart API functions to prevent race conditions and ensure correct market URLs
 */

class MarketCartUtils {
  constructor() {
    this.marketDetectionReady = false;
    this.currentMarket = null;
    this.init();
  }

  init() {
    // Immediately detect market synchronously to prevent race conditions
    this.currentMarket = this.getCurrentMarket();
    this.marketDetectionReady = true;

    // Set up market change detection
    this.setupMarketChangeDetection();
    
    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('market-detection-ready'));
  }

  /**
   * Set up market change detection to clear caches when market changes
   */
  setupMarketChangeDetection() {
    const lastMarketKey = 'last-detected-market';
    const lastMarket = localStorage.getItem(lastMarketKey);
    const currentMarket = this.getCurrentMarket();
    
    if (lastMarket && lastMarket !== currentMarket) {
      // Clear all cart caches when market changes
      this.clearAllCartCaches();
      
      // Dispatch market change event
      document.dispatchEvent(new CustomEvent('market:changed', {
        detail: { 
          from: lastMarket, 
          to: currentMarket 
        }
      }));
    }
    
    // Store current market
    localStorage.setItem(lastMarketKey, currentMarket);
    
    // Set up periodic market checking
    setInterval(() => {
      const newMarket = this.getCurrentMarket();
      if (newMarket !== this.currentMarket) {
        this.currentMarket = newMarket;
        this.clearAllCartCaches();
        
        document.dispatchEvent(new CustomEvent('market:changed', {
          detail: { 
            from: this.currentMarket, 
            to: newMarket 
          }
        }));
      }
    }, 2000); // Check every 2 seconds
  }

  /**
   * Wait for market detection to be ready
   * @returns {Promise<void>}
   */
  async waitForMarketDetection() {
    // No longer waiting for MarketCartValidator - use direct detection
    return Promise.resolve();
  }

  /**
   * Get current market with fallback
   * @returns {string} Market code (e.g., 'GB', 'FR', 'DE')
   */
  getCurrentMarket() {
    // Direct market detection without dependencies
    let market = null;
    const sources = [];

    // Method 1: Check Shopify object
    if (window.Shopify?.country) {
      market = window.Shopify.country;
      sources.push(`Shopify.country: ${market}`);
    }

    // Method 2: Check localization data
    if (!market && window.localization?.country?.iso_code) {
      market = window.localization.country.iso_code;
      sources.push(`localization.country: ${market}`);
    }

    // Method 3: Check URL pattern (e.g. /de-de/, /fr-fr/)
    if (!market) {
      const urlMatch = window.location.pathname.match(/^\/([a-z]{2})-([a-z]{2})\//);
      if (urlMatch) {
        market = urlMatch[2].toUpperCase();
        sources.push(`URL path: ${urlMatch[0]}`);
      }
    }

    // Method 4: Check document data attributes
    if (!market) {
      const dataMarket = document.documentElement.getAttribute('data-market') || 
                        document.documentElement.getAttribute('data-country');
      if (dataMarket) {
        market = dataMarket;
        sources.push(`Data attribute: ${dataMarket}`);
      }
    }

    // Method 5: Check hostname for subdomain
    if (!market) {
      const hostname = window.location.hostname;
      if (hostname.includes('.fr')) {
        market = 'FR';
        sources.push(`Hostname: ${hostname}`);
      } else if (hostname.includes('.de')) {
        market = 'DE';
        sources.push(`Hostname: ${hostname}`);
      } else if (hostname.includes('.at')) {
        market = 'AT';
        sources.push(`Hostname: ${hostname}`);
      } else if (hostname.includes('.ie')) {
        market = 'IE';
        sources.push(`Hostname: ${hostname}`);
      }
    }

    // Method 6: Check URL parameters
    if (!market) {
      const urlParams = new URLSearchParams(window.location.search);
      const marketParam = urlParams.get('market');
      if (marketParam) {
        market = marketParam.toUpperCase();
        sources.push(`URL param: ${marketParam}`);
      }
    }

    // Method 7: Check theme object if available
    if (!market && typeof theme !== 'undefined' && theme.localization) {
      market = theme.localization.country.iso_code;
      sources.push(`theme.localization: ${market}`);
    }

    // Method 8: Check for Shopify market in meta tags
    if (!market) {
      const marketMeta = document.querySelector('meta[name="shopify-market"]');
      if (marketMeta) {
        market = marketMeta.getAttribute('content');
        sources.push(`Meta tag: ${market}`);
      }
    }

    // Method 9: Check for country in meta tags
    if (!market) {
      const countryMeta = document.querySelector('meta[name="shopify-country"]');
      if (countryMeta) {
        market = countryMeta.getAttribute('content');
        sources.push(`Country meta: ${market}`);
      }
    }

    // Method 10: Check for locale in document
    if (!market) {
      const locale = document.documentElement.getAttribute('lang');
      if (locale) {
        const localeMatch = locale.match(/^([a-z]{2})/);
        if (localeMatch) {
          const countryCode = localeMatch[1].toUpperCase();
          // Map common locale codes to countries
          const localeMap = {
            'FR': 'FR',
            'DE': 'DE', 
            'AT': 'AT',
            'IE': 'IE',
            'ES': 'ES',
            'IT': 'IT',
            'NL': 'NL',
            'PL': 'PL',
            'RO': 'RO',
            'SK': 'SK',
            'SL': 'SL',
            'LT': 'LT'
          };
          if (localeMap[countryCode]) {
            market = localeMap[countryCode];
            sources.push(`Locale: ${locale}`);
          }
        }
      }
    }

    const finalMarket = market?.toUpperCase() || 'GB';
    

    return finalMarket;
  }

  /**
   * Get market-aware URL prefix
   * @returns {string} URL prefix for current market
   */
  getMarketPrefix() {
    const market = this.getCurrentMarket();
    return market === 'GB' ? '' : `/${market.toLowerCase()}-${market.toLowerCase()}`;
  }

  /**
   * Get market-aware cart API URL (for GET requests only)
   * @param {string} endpoint - Cart API endpoint (e.g., '/cart.js')
   * @returns {string} Market-aware cart API URL
   */
  getMarketAwareCartUrl(endpoint) {
    // Only apply market prefix to GET requests (data fetching)
    if (endpoint.includes('/cart.js') || endpoint.includes('?sections=')) {
      const prefix = this.getMarketPrefix();
      return `${prefix}${endpoint}`;
    }
    // POST requests don't need market prefix
    return endpoint;
  }

  /**
   * Get market-aware sections URL
   * @param {Array<string>} sections - Array of section IDs
   * @returns {string} Market-aware sections URL
   */
  getMarketAwareSectionsUrl(sections) {
    const prefix = this.getMarketPrefix();
    return `${prefix}?sections=${sections.join(',')}`;
  }

  /**
   * Get market-aware cart data with caching
   * @param {boolean} force - Bypass cache
   * @returns {Promise<Object>} Cart data
   */
  async getMarketAwareCartData(force = false) {
    // Market detection is now synchronous, no need to wait
    const currentMarket = this.getCurrentMarket();
    const cacheKey = `cart-data-${currentMarket}`;
    const now = Date.now();
    const cacheExpiry = 10000; // Reduced to 10 seconds for better currency sync

    // Check cache
    if (!force) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp, market } = JSON.parse(cached);
        if (now - timestamp < cacheExpiry && market === currentMarket) {
          return data;
        }
      }
    }

    // Fetch fresh data
    const url = this.getMarketAwareCartUrl('/cart.js');
    const response = await fetch(url, { credentials: 'same-origin' });
    
    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Validate currency matches expected market currency
    const expectedCurrency = this.getExpectedCurrencyForMarket(currentMarket);
    
    // Cache the data with market info
    sessionStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: now,
      market: currentMarket
    }));

    return data;
  }

  /**
   * Fetch market-aware sections
   * @param {Array<string>} sections - Array of section IDs
   * @returns {Promise<Object>} Sections data
   */
  async fetchMarketAwareSections(sections) {
    // Market detection is now synchronous, no need to wait
    const currentMarket = this.getCurrentMarket();
    const url = this.getMarketAwareSectionsUrl(sections);
    
    const response = await fetch(url, { credentials: 'same-origin' });
    
    if (!response.ok) {
      throw new Error(`Sections fetch failed: ${response.status}`);
    }

    const data = await response.json();
    
    return data;
  }

  /**
   * Clear cart cache for current market
   */
  clearCartCache() {
    const cacheKey = `cart-data-${this.getCurrentMarket()}`;
    sessionStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cart caches (for market switches)
   */
  clearAllCartCaches() {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('cart-data-')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Validate cart data currency matches current market
   * @param {Object} cartData - Cart data to validate
   * @returns {boolean} True if currency is correct for current market
   */
  validateCartCurrency(cartData) {
    const currentMarket = this.getCurrentMarket();
    const expectedCurrency = this.getExpectedCurrencyForMarket(currentMarket);
    const actualCurrency = cartData.currency;
    
    const isValid = actualCurrency === expectedCurrency;
    
    if (!isValid) {
      console.warn('Currency mismatch detected:', {
        market: currentMarket,
        expected: expectedCurrency,
        actual: actualCurrency,
        cartTotal: cartData.total_price
      });
    }
    
    return isValid;
  }

  /**
   * Get expected currency for a market
   * @param {string} market - Market code (e.g., 'GB', 'FR', 'DE')
   * @returns {string} Expected currency code
   */
  getExpectedCurrencyForMarket(market) {
    const currencyMap = {
      'GB': 'GBP',
      'FR': 'EUR', 
      'DE': 'EUR',
      'AT': 'EUR',
      'IE': 'EUR'
    };
    
    return currencyMap[market] || 'GBP';
  }

  /**
   * Force refresh cart data if currency is incorrect
   * @param {Object} cartData - Current cart data
   * @returns {Promise<Object>} Fresh cart data if currency was wrong
   */
  async ensureCorrectCurrency(cartData) {
    if (!this.validateCartCurrency(cartData)) {
      return await this.getMarketAwareCartData(true);
    }
    
    return cartData;
  }

  /**
   * Force clear all caches and fetch fresh cart data
   * Used when page loads to ensure correct currency from start
   * @returns {Promise<Object>} Fresh cart data
   */
  async forceFreshCartData() {
    // Clear all caches
    this.clearAllCartCaches();
    
    // Force fresh fetch
    const cartData = await this.getMarketAwareCartData(true);
    
    return cartData;
  }

}

// Create global instance
window.MarketCartUtils = new MarketCartUtils();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MarketCartUtils;
}
